/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertCircle, XCircle, Loader2, Wallet } from "lucide-react";

const VERDICT_STYLE = {
  APPROVED: { badge: "bg-success/15 text-success border-success/40", icon: CheckCircle2, label: "Eligible — Approve" },
  REFER:    { badge: "bg-warning/15 text-warning border-warning/40", icon: AlertCircle, label: "Refer — Review needed" },
  REJECTED: { badge: "bg-destructive/15 text-destructive border-destructive/40", icon: XCircle, label: "Not Eligible" },
};

/**
 * EligibilityPreview
 *
 * Two flavours:
 *
 * 1) Live mode — pass `inputs` (income, expense, EMI, tenor, ROI, amount).
 *    The component will POST /api/eligibility/compute (debounced 350ms) and render the verdict card.
 *    Optional `onResult(snapshot)` callback fires whenever a new snapshot is computed.
 *
 * 2) Snapshot mode — pass `snapshot` (already computed result from
 *    application.eligibility_snapshot) and the component will render without making any API call.
 *    Useful inside ApplicationDetailPage to render historical/persisted eligibility.
 *
 * `compact={true}` removes the section header and gives a tighter layout (good for inline use).
 */
export default function EligibilityPreview({ inputs, snapshot, compact = false, onResult, dataTestid = "eligibility-preview" }) {
  const [data, setData] = useState(snapshot || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (snapshot) { setData(snapshot); return; }
    if (!inputs) return;
    const { monthly_income, tenor_months, interest_rate_pa } = inputs;
    if (!monthly_income || monthly_income <= 0 || !tenor_months || tenor_months <= 0 || !interest_rate_pa || interest_rate_pa <= 0) {
      setData(null);
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const payload = {
          monthly_income: Number(monthly_income),
          monthly_expense: Number(inputs.monthly_expense || 0),
          existing_emi: Number(inputs.existing_emi || 0),
          tenor_months: Number(tenor_months),
          interest_rate_pa: Number(interest_rate_pa),
          proposed_amount_lakhs: inputs.proposed_amount_lakhs ? Number(inputs.proposed_amount_lakhs) : null,
          max_foir_pct: Number(inputs.max_foir_pct || 50),
        };
        if (inputs.product_code) payload.product_code = inputs.product_code;
        if (inputs.customer_age) payload.customer_age = Number(inputs.customer_age);
        const r = await api.post("/eligibility/compute", payload);
        setData(r.data);
        onResult && onResult(r.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [
    snapshot,
    inputs?.monthly_income,
    inputs?.monthly_expense,
    inputs?.existing_emi,
    inputs?.tenor_months,
    inputs?.interest_rate_pa,
    inputs?.proposed_amount_lakhs,
    inputs?.max_foir_pct,
    inputs?.product_code,
    inputs?.customer_age,
  ]);

  if (loading && !data) {
    return (
      <Card className="p-3 border-dashed" data-testid={`${dataTestid}-loading`}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" />Computing eligibility…</div>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card className="p-3 border-dashed" data-testid={`${dataTestid}-empty`}>
        <div className="text-xs text-muted-foreground flex items-center gap-2"><Wallet className="h-3.5 w-3.5" />Enter Net Monthly Income to preview FOIR-based eligibility.</div>
      </Card>
    );
  }

  const verdictMeta = VERDICT_STYLE[data.verdict] || VERDICT_STYLE.REFER;
  const VIcon = verdictMeta.icon;
  const cap = data.input_summary?.max_foir_pct ?? 50;

  return (
    <Card className={`p-3 space-y-2 border ${verdictMeta.badge}`} data-testid={dataTestid}>
      {!compact && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <VIcon className="h-4 w-4" />
            <div className="font-semibold text-sm" data-testid={`${dataTestid}-verdict-${data.verdict}`}>{verdictMeta.label}</div>
          </div>
          <Badge variant="outline" className="text-[10px]">FOIR {data.current_foir_pct}% / cap {cap}%</Badge>
        </div>
      )}
      {compact && (
        <div className="flex items-center gap-2">
          <VIcon className="h-4 w-4" />
          <div className="text-xs font-semibold" data-testid={`${dataTestid}-verdict-${data.verdict}`}>{verdictMeta.label}</div>
          <Badge variant="outline" className="text-[10px] ml-auto">FOIR {data.current_foir_pct}% / cap {cap}%</Badge>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <Stat k="Net Disposable" v={`₹${Number(data.net_disposable).toLocaleString("en-IN")}`} />
        <Stat k="Max EMI Allowed" v={`₹${Number(data.max_eligible_emi).toLocaleString("en-IN")}`} />
        <Stat k="Max Eligible Amount" v={`₹${data.max_eligible_amount_lakhs}L`} testid={`${dataTestid}-max-eligible-amount`} />
        {data.proposed_emi > 0 && (
          <Stat k="Proposed EMI" v={`₹${Number(data.proposed_emi).toLocaleString("en-IN")}`} testid={`${dataTestid}-proposed-emi`} />
        )}
      </div>
      {data.reasons?.length > 0 && (
        <ul className="text-xs space-y-1 pt-1">
          {data.reasons.map((r, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className={`mt-0.5 inline-block h-1.5 w-1.5 rounded-full ${r.status === "PASS" ? "bg-success" : r.status === "WARN" ? "bg-warning" : "bg-destructive"}`} />
              <span className={r.status === "PASS" ? "text-success" : r.status === "WARN" ? "text-warning" : "text-destructive"}><span className="font-semibold mono">{r.check}</span></span>
              <span className="text-muted-foreground">— {r.msg}</span>
            </li>
          ))}
        </ul>
      )}
      {data.computed_at && (
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground pt-1 border-t border-border/40">
          Computed {new Date(data.computed_at).toLocaleString("en-IN")} · source <span className="mono">{data.source || "live"}</span>{data.computed_by ? ` · by ${data.computed_by}` : ""}
        </div>
      )}
    </Card>
  );
}

function Stat({ k, v, testid }) {
  return (
    <div className="rounded-md bg-background/60 border border-border/60 px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-medium">{k}</div>
      <div className="text-xs font-semibold tabular" data-testid={testid}>{v}</div>
    </div>
  );
}
